<template>
  <div class="page-shell">
    <header class="page-header" v-if="hasHeader">
      <div class="title-row" v-if="showHeader">
        <div class="system-title">{{ systemTitle }}</div>
        <slot name="header-extra" />
      </div>
      <el-breadcrumb v-if="showBreadcrumb" separator="/" class="breadcrumb">
        <el-breadcrumb-item v-for="(item, idx) in autoBreadcrumbs" :key="idx">
          <a v-if="idx < autoBreadcrumbs.length - 1" @click.prevent="goCrumb(item, idx)">{{ item.label }}</a>
          <span v-else>{{ item.label }}</span>
        </el-breadcrumb-item>
      </el-breadcrumb>
      <div class="action-bar" v-if="visibleActions.length">
        <el-space wrap :size="10">
          <el-button
            v-for="action in visibleActions"
            :key="action.label"
            :type="action.type || 'default'"
            :icon="action.icon"
            @click="action.onClick && action.onClick()"
          >
            {{ action.label }}
          </el-button>
        </el-space>
      </div>
    </header>
    <section class="page-body">
      <slot />
    </section>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'

const props = defineProps({
  systemTitle: { type: String, default: '应用系统' },
  breadcrumbs: { type: Array, default: () => [] },
  actions: { type: Array, default: () => [] },
  canUse: { type: Function, default: () => true },
  showHeader: { type: Boolean, default: true },
  showBreadcrumb: { type: Boolean, default: true },
})

const router = useRouter()
const route = useRoute()

const autoBreadcrumbs = computed(() => {
  if (props.breadcrumbs && props.breadcrumbs.length) return props.breadcrumbs
  if (route.meta && Array.isArray(route.meta.breadcrumbs) && route.meta.breadcrumbs.length) return route.meta.breadcrumbs
  const fromMatched = route.matched
    .filter((m) => m.meta && (m.meta.title || m.name))
    .map((m) => ({ label: m.meta.title || m.name || '', path: m.path }))
  return fromMatched
})

const visibleActions = computed(() => props.actions.filter((a) => props.canUse(a)))
const hasHeader = computed(() => props.showHeader || props.showBreadcrumb || visibleActions.value.length > 0)
const goCrumb = (item, idx) => {
  const list = autoBreadcrumbs.value || []
  if (idx >= list.length - 1) return
  const target = item.path
  if (!target) return
  if (typeof target === 'object') {
    router.push(target)
    return
  }
  const normalized = target.startsWith('/admin') ? target : `/admin/${target.replace(/^\//, '')}`
  if (normalized === '/admin/menu' || normalized === '/admin/home') return
  router.push(normalized)
}
</script>

<style scoped>
.page-shell {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--bymy-color-bg-shell);
  width: 100%;
  min-width: 0;
}
.page-header {
  background: var(--bymy-color-bg-card);
  padding: 12px 16px;
  border-bottom: 1px solid var(--bymy-color-border);
  box-shadow: var(--bymy-shadow-header);
}
.title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}
.system-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--bymy-color-text);
}
.breadcrumb {
  font-size: 13px;
  color: var(--bymy-color-text-secondary);
  margin-bottom: 8px;
}
.action-bar {
  padding: 4px 0 6px;
  display: flex;
  justify-content: flex-end;
}
.page-body {
  flex: 1;
  padding: 12px 16px 16px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  overflow: auto;
}
</style>
